<script setup lang="ts">
import { useUserStore } from '@/store/users'

const emit = defineEmits<{
  (
    e: 'setCount',
    d: { male: number; female: number; total: number; live: number },
  ): void
}>()

const userStore = useUserStore()

const loading = ref(false)
const userPackageModal = ref(false)

const startDate = ref<string>('')
const endDate = ref<string>('')
const appliedRange = ref<{ start: string; end: string } | null>(null)

const toUTCRange = (date: string, type: 'start' | 'end'): string => {
  if (!date) return ''
  if (type === 'start') {
    return new Date(`${date}T00:00:00+03:00`).toISOString()
  } else {
    return new Date(`${date}T23:59:59.999+03:00`).toISOString()
  }
}

const applyFilter = () => {
  appliedRange.value =
    startDate.value || endDate.value
      ? {
          start: toUTCRange(startDate.value, 'start'),
          end: toUTCRange(endDate.value, 'end'),
        }
      : null
  fetchUserCounts()
}

const clearFilter = () => {
  startDate.value = ''
  endDate.value = ''
  appliedRange.value = null
  fetchUserCounts()
}

const formattedRange = computed(() => {
  if (!appliedRange.value) return null
  const fmt = (isoStr: string) =>
    isoStr ? new Date(isoStr).toLocaleDateString('tr-TR') : '...'
  return `${fmt(appliedRange.value.start)} – ${fmt(appliedRange.value.end)}`
})

const selectedUserType = ref({
  name: '',
  free: 0,
  premium: 0,
  student: 0,
})

const userCount = reactive({
  male: 0,
  maleFree: 0,
  malePremium: 0,
  maleStudent: 0,
  female: 0,
  femaleFree: 0,
  femalePremium: 0,
  femaleStudent: 0,
  testmale: 0,
  testfemale: 0,
  total: 0,
  live: 0,
  notSpecified: 0,
  notSpecifiedFree: 0,
  notSpecifiedPremium: 0,
  notSpecifiedStudent: 0,
  isTestUser: 0,
})

const showUserPackageCount = (title: string) => {
  if (title.includes('Test') || title.includes('Canlı')) {
    return
  }
  if (title.includes('Kadın')) {
    selectedUserType.value.name = 'Kadın'
    selectedUserType.value.free = userCount.femaleFree
    selectedUserType.value.premium = userCount.femalePremium
    selectedUserType.value.student = userCount.femaleStudent
    userPackageModal.value = true
  } else if (title.includes('Erkek')) {
    selectedUserType.value.name = 'Erkek'
    selectedUserType.value.free = userCount.maleFree
    selectedUserType.value.premium = userCount.malePremium
    selectedUserType.value.student = userCount.maleStudent
    userPackageModal.value = true
  } else if (title.includes('Toplam')) {
    selectedUserType.value.name = 'Toplam'
    selectedUserType.value.free =
      userCount.femaleFree + userCount.maleFree + userCount.notSpecifiedFree
    selectedUserType.value.premium =
      userCount.femalePremium +
      userCount.malePremium +
      userCount.notSpecifiedPremium
    selectedUserType.value.student =
      userCount.femaleStudent +
      userCount.maleStudent +
      userCount.notSpecifiedStudent
    userPackageModal.value = true
  } else {
    selectedUserType.value.name = 'Belirsiz'
    selectedUserType.value.free = userCount.notSpecifiedFree
    selectedUserType.value.premium = userCount.notSpecifiedPremium
    selectedUserType.value.student = userCount.notSpecifiedStudent
    userPackageModal.value = true
  }
}

const fetchUserCounts = async () => {
  loading.value = true

  const params: { startDate?: string; endDate?: string } = {}
  if (appliedRange.value?.start) params.startDate = appliedRange.value.start
  if (appliedRange.value?.end) params.endDate = appliedRange.value.end

  const response = await userStore.fetchUserCount(params)

  loading.value = false

  if (response?.status !== 200) return

  userCount.male = response.data?.male ?? 0
  userCount.malePremium = response.data?.malePremium ?? 0
  userCount.maleStudent = response.data?.maleStudent ?? 0
  userCount.maleFree =
    response.data?.male -
      (response.data?.maleStudent + response.data?.malePremium) ?? 0
  userCount.female = response.data?.female ?? 0
  userCount.femalePremium = response.data?.femalePremium ?? 0
  userCount.femaleStudent = response.data?.femaleStudent ?? 0
  userCount.femaleFree =
    response.data?.female -
      (response.data?.femalePremium + response.data?.femaleStudent) ?? 0
  userCount.total = response.data?.total ?? 0
  userCount.live = response.data?.live ?? 0
  userCount.notSpecified = response.data?.notSpecified ?? 0
  userCount.notSpecifiedFree =
    response.data?.notSpecified -
      (response.data?.notSpecifiedStudent +
        response.data?.notSpecifiedPremium) ?? 0
  userCount.notSpecifiedPremium = response.data?.notSpecifiedPremium ?? 0
  userCount.notSpecifiedStudent = response.data?.notSpecifiedStudent ?? 0
  userCount.testmale = response.data?.testmale ?? 0
  userCount.testfemale = response.data?.testfemale ?? 0

  emit('setCount', toRaw(userCount))
}

fetchUserCounts()

const { pause, resume } = useIntervalFn(() => {
  if (!appliedRange.value) fetchUserCounts()
}, 30 * 1000)

const staticData = computed(() => [
  {
    title: 'Kayıtlı Erkek Kullanıcı',
    stats: userCount.male,
    color: 'primary',
    icon: 'tabler-gender-male',
    tooltip: 'Erkek paket bilgileri',
  },
  {
    title: 'Kayıtlı Kadın Kullanıcı',
    stats: userCount.female,
    color: '#db2777',
    icon: 'tabler-gender-female',
    tooltip: 'Kadın paket bilgileri',
  },
  {
    title: 'Kayıtlı Belirsiz Kullanıcı',
    stats: userCount.notSpecified,
    color: 'gray',
    icon: 'icons8:gender',
    tooltip: 'Belirsiz paket bilgileri',
  },
  {
    title: 'Toplam Kullanıcı',
    stats: userCount.total,
    color: 'success',
    icon: 'tabler-gender-bigender',
    tooltip: 'Toplam paket bilgileri',
  },
  {
    title: 'Canlı Kullanıcı Sayısı',
    stats: userCount.live,
    color: 'warning',
    icon: 'tabler-live-photo',
  },
  {
    title: 'Erkek Test Kullanıcı Sayısı',
    stats: userCount.testmale,
    color: 'secondary',
    icon: 'fluent-mdl2:test-user-solid',
  },
  {
    title: 'Kadın Test Kullanıcı Sayısı',
    stats: userCount.testfemale,
    color: 'secondary',
    icon: 'fluent-mdl2:test-user-solid',
  },
])
</script>

<template>
  <VRow class="mb-4">
    <VCol cols="12">
      <VCard>
        <VCardText class="d-flex align-center flex-wrap gap-4">
          <div class="d-flex flex-column gap-1">
            <span class="text-sm text-disabled">Başlangıç Tarihi</span>
            <AppDateTimePicker
              v-model="startDate"
              placeholder="gg.aa.yyyy"
              :config="{
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd.m.Y',
                maxDate: endDate || 'today',
              }"
              style="width: 160px"
            />
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="text-sm text-disabled">Bitiş Tarihi</span>
            <AppDateTimePicker
              v-model="endDate"
              placeholder="gg.aa.yyyy"
              :config="{
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd.m.Y',
                minDate: startDate || undefined,
                maxDate: 'today',
              }"
              style="width: 160px"
            />
          </div>

          <div class="d-flex align-center gap-2 mt-4">
            <VBtn variant="tonal" @click="applyFilter">Filtrele</VBtn>
            <VBtn
              variant="text"
              color="secondary"
              @click="clearFilter"
              :disabled="!appliedRange"
            >
              Temizle
            </VBtn>
            <VChip v-if="formattedRange" color="info" size="small">
              {{ formattedRange }}
            </VChip>
          </div>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <VRow>
    <VCol v-for="data in staticData" :key="data.title" cols="12" sm="6" lg="3">
      <VCard>
        <VCardText class="d-flex justify-space-between">
          <div>
            <p style="height: 39.5px">{{ data.title }}</p>

            <h6 class="text-h4 mt-1">
              <VProgressCircular
                v-if="loading"
                indeterminate
                width="2"
                size="26"
              />

              <template v-else> {{ data.stats }}</template>
            </h6>
          </div>
          <VTooltip :text="data.tooltip">
            <template #activator="{ props }">
              <VAvatar
                v-bind="data.tooltip ? props : ''"
                rounded
                variant="tonal"
                :id="data.title"
                :color="data.color"
                :icon="data.icon"
                :class="data.tooltip ? 'cursor-pointer' : ''"
                @click="showUserPackageCount(data.title)"
              />
            </template>
          </VTooltip>
        </VCardText>
      </VCard>
    </VCol>
    <VDialog v-model="userPackageModal" width="900">
      <DialogCloseBtn @click="userPackageModal = false" />
      <VCard
        :title="selectedUserType.name + ' Paket Bilgileri'"
        height="250"
        class="px-4"
      >
        <VRow class="mt-4">
          <VCol cols="12" md="4">
            <VCard
              ><VCardText class="d-flex justify-space-between">
                <div>
                  <p style="height: 39.5px">
                    {{ `${selectedUserType.name} Premium Kullanıcı` }}
                  </p>
                  <h6 class="text-h4 mt-1">
                    <VProgressCircular
                      v-if="loading"
                      indeterminate
                      width="2"
                      size="26"
                    />

                    <template v-else> {{ selectedUserType.premium }}</template>
                  </h6>
                </div>
                <VAvatar
                  rounded
                  variant="tonal"
                  color="#416FFC"
                  icon="tabler-rosette"
                />
              </VCardText>
            </VCard>
          </VCol>
          <VCol cols="12" md="4">
            <VCard
              ><VCardText class="d-flex justify-space-between">
                <div>
                  <p style="height: 39.5px">
                    {{ `${selectedUserType.name} Öğrenci Kullanıcı` }}
                  </p>
                  <h6 class="text-h4 mt-1">
                    <VProgressCircular
                      v-if="loading"
                      indeterminate
                      width="2"
                      size="26"
                    />

                    <template v-else> {{ selectedUserType.student }}</template>
                  </h6>
                </div>
                <VAvatar
                  rounded
                  variant="tonal"
                  color="#eab308"
                  icon="tabler-book"
                /> </VCardText
            ></VCard>
          </VCol>
          <VCol cols="12" md="4">
            <VCard
              ><VCardText class="d-flex justify-space-between">
                <div>
                  <p style="height: 39.5px">
                    {{ `${selectedUserType.name} Bedava Kullanıcı` }}
                  </p>
                  <h6 class="text-h4 mt-1">
                    <VProgressCircular
                      v-if="loading"
                      indeterminate
                      width="2"
                      size="26"
                    />
                    <template v-else> {{ selectedUserType.free }}</template>
                  </h6>
                </div>
                <VAvatar
                  rounded
                  variant="tonal"
                  color="secondary"
                  icon="tabler-discount-off"
                /> </VCardText
            ></VCard>
          </VCol>
        </VRow>
      </VCard>
    </VDialog>
  </VRow>
</template>
